/**
 * PROC-06 Cadence bimodality and drift (INV-PACK-06, ENGINE-SPEC §4; change C-PROC06-DRIFT).
 *
 * (a) Bimodality: the share of prose sentences with ≤ 4 or ≥ 50 words (a proportion, so it feeds
 *     the paragraph-level segmentation: perParagraph k = extreme sentences, e = sentences). The
 *     estimate is the shrunken, overdispersion-aware proportion of the rate framework.
 *     Run-on units (> 120 words, R12) are excluded, the conservative choice.
 * (b) Signed drift: Pearson r between the ordinal of each natural unit and its mean sentence
 *     length. Units are the chapters with ≥ 15 sentences (≥ 6 needed), else the scenes with ≥ 15
 *     sentences (≥ 6 needed); the pack's ten-equal-block fallback is REMOVED (ED-5), so with
 *     fewer than 6 units the drift term abstains. Only lengthening drift counts: the scored
 *     value is the one-sided lower 90% bound tanh(atanh r − 1.2816/√(J−3)) clipped at 0.
 * Display score = mean of the sub-scores ramp(a, [[0.18,0],[0.30,100]]) and
 * ramp(b, [[0.30,0],[0.70,100]]), or (a) alone when drift abstains.
 */
import { SENT_RUNON } from '../document/types.ts';
import type { UnitRange } from '../document/types.ts';
import { ramp } from '../util/stats.ts';
import {
	type RateFit,
	abstainResult,
	derivePrior,
	fitRate,
	languageGate,
	overdispersion,
	scoredResult,
} from './framework.ts';
import { fmt, makeEvidence, proseSentenceList } from './helpers.ts';
import type { ChannelDef, ChannelImpl, ChannelResult, Ramp, RunContext } from './types.ts';

const RAMP_A: Ramp = [
	[0.18, 0],
	[0.3, 100],
];
const RAMP_B: Ramp = [
	[0.3, 0],
	[0.7, 100],
];
export const SHORT = 4;
export const LONG = 50;
const MIN_UNITS = 6;
const MIN_UNIT_SENTENCES = 15;
const Z90 = 1.2816;
const Z95 = 1.645;

export const proc06Def: ChannelDef = {
	id: 'PROC-06',
	name: 'Cadence bimodality and drift',
	family: 'RHYTHM',
	axis: 'authorship',
	registers: ['prose'],
	direction: 1,
	oneWay: true,
	estimator: 'proportion',
	features: [
		{ id: 'bimodality', label: 'Share of sentences ≤ 4 or ≥ 50 words' },
		{ id: 'drift', label: 'Lengthening drift (lower 90% bound of r)' },
	],
	minExposure: 200,
	exposureUnit: 'sentences',
	lang: 'agnostic',
	ramp: RAMP_A,
	unitScale: 1,
	unitLabel: 'share of ≤ 4 / ≥ 50-word sentences',
	prior: derivePrior(RAMP_A, 1, 'sentences', 1.2),
	weight: 1.05,
	nd: 0.15,
	changes: ['C-PROC06-DRIFT'],
};

const atanh = (x: number): number => 0.5 * Math.log((1 + x) / (1 - x));

export interface Proc06Parts {
	/** Per paragraph: extreme sentences, sentences, and the sentence count and word sum used for the unit means. */
	k: Float64Array;
	e: Float64Array;
	nPara: Float64Array;
	wPara: Float64Array;
	N: number;
	nShort: number;
	nLong: number;
}

export interface Proc06Fit {
	K: number;
	phi: number;
	fit: RateFit;
	subA: number;
	r: number | null;
	J: number;
	unitKind: string;
	subB: number | null;
	bound: number;
	score: number;
	scoreCi90: [number, number];
	kappa: number;
	notes: string[];
}

/**
 * The document value from the per-paragraph sufficient statistics: (a) the shrunken extreme-sentence share with its
 * overdispersion (`frozenPhi` replaces the re-estimated φ in the bench, BENCH-V2-SPEC §5.1), (b) the drift over the natural
 * units. Shared by `run` and the bench's `DocState`.
 */
export function proc06Fit(
	parts: Proc06Parts,
	chapters: UnitRange[],
	scenes: UnitRange[],
	frozenPhi?: number
): Proc06Fit {
	const def = proc06Def;
	const { k, e, nPara, wPara, N, nShort, nLong } = parts;
	const P = k.length;
	const notes: string[] = [];
	const K = nShort + nLong;
	const phi = frozenPhi ?? overdispersion(k, e, K, N, def.prior.phi0, true);
	const fit = fitRate(def, K, N, phi, true);
	const subA = fit.score;

	// (b) signed drift over natural units
	const prefN = new Float64Array(P + 1);
	const prefW = new Float64Array(P + 1);
	for (let p = 0; p < P; p++) {
		prefN[p + 1] = (prefN[p] as number) + (nPara[p] as number);
		prefW[p + 1] = (prefW[p] as number) + (wPara[p] as number);
	}
	const unitMeans = (list: UnitRange[]): number[] => {
		const out: number[] = [];
		for (const u of list) {
			const n = (prefN[u.paraEnd] as number) - (prefN[u.paraStart] as number);
			if (n < MIN_UNIT_SENTENCES) continue;
			out.push(((prefW[u.paraEnd] as number) - (prefW[u.paraStart] as number)) / n);
		}
		return out;
	};
	let means = unitMeans(chapters);
	let unitKind = 'chapters';
	if (means.length < MIN_UNITS) {
		means = unitMeans(scenes);
		unitKind = 'scenes';
	}
	const J = means.length;
	let r: number | null = null;
	if (J >= MIN_UNITS) {
		const mx = (J - 1) / 2;
		const my = means.reduce((a, b) => a + b, 0) / J;
		let num = 0;
		let dx = 0;
		let dy = 0;
		for (let i = 0; i < J; i++) {
			const a = i - mx;
			const b = (means[i] as number) - my;
			num += a * b;
			dx += a * a;
			dy += b * b;
		}
		if (dx > 0 && dy > 0) r = num / Math.sqrt(dx * dy);
	}
	let subB: number | null = null;
	let bound = 0;
	let subBCi: [number, number] = [0, 0];
	if (r !== null) {
		const rc = Math.max(-0.999999, Math.min(0.999999, r));
		const z = atanh(rc);
		const sez = 1 / Math.sqrt(J - 3);
		bound = Math.max(0, Math.tanh(z - Z90 * sez));
		subB = ramp(bound, RAMP_B);
		const lo = Math.max(0, Math.tanh(z - Z95 * sez));
		const hi = Math.max(0, Math.tanh(z + Z95 * sez));
		subBCi = [ramp(lo, RAMP_B), ramp(hi, RAMP_B)];
	} else if (J < MIN_UNITS) {
		notes.push(
			`drift abstains: ${J} natural units with at least ${MIN_UNIT_SENTENCES} sentences < ${MIN_UNITS}`
		);
	} else {
		notes.push('drift abstains: no variation in unit means');
	}

	const score = subB === null ? subA : (subA + subB) / 2;
	const scoreCi90: [number, number] =
		subB === null
			? fit.scoreCi90
			: [(fit.scoreCi90[0] + subBCi[0]) / 2, (fit.scoreCi90[1] + subBCi[1]) / 2];
	const kappa = subB === null ? fit.kappa : (fit.kappa + J / (J + 6)) / 2;

	return { K, phi, fit, subA, r, J, unitKind, subB, bound, score, scoreCi90, kappa, notes };
}

function run(ctx: RunContext): ChannelResult {
	const def = proc06Def;
	const gate = languageGate(def, ctx);
	if (gate.abstain) return abstainResult(def, gate.abstain);
	const notes: string[] = [];
	if (gate.note) notes.push(gate.note);
	const { doc } = ctx;
	const list = proseSentenceList(doc);
	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	// per-paragraph sentence count and word sum (for unit means), and the extreme-sentence evidence
	const nPara = new Float64Array(P);
	const wPara = new Float64Array(P);
	const ev = makeEvidence();
	let N = 0;
	let nShort = 0;
	let nLong = 0;
	for (let i = 0; i < list.count; i++) {
		const w = list.words[i] as number;
		if (w === 0 || (list.flags[i] as number) & SENT_RUNON) continue;
		N++;
		const p = list.para[i] as number;
		e[p] = (e[p] as number) + 1;
		nPara[p] = (nPara[p] as number) + 1;
		wPara[p] = (wPara[p] as number) + w;
		const isShort = w <= SHORT;
		const isLong = w >= LONG;
		if (isShort || isLong) {
			k[p] = (k[p] as number) + 1;
			if (isShort) nShort++;
			else nLong++;
			const s = list.sent[i] as number;
			ev.add(
				doc.sentences.charStart[s] as number,
				doc.sentences.charEnd[s] as number,
				isShort ? 0 : 1,
				1
			);
		}
	}
	if (N < def.minExposure)
		return abstainResult(def, `insufficient text: ${N} sentences < ${def.minExposure}`, notes);
	const pf = proc06Fit({ k, e, nPara, wPara, N, nShort, nLong }, doc.chapters, doc.scenes);
	const { K, phi, fit, subA, r, J, unitKind, subB, bound, score, scoreCi90, kappa } = pf;
	notes.push(...pf.notes);

	const summary = [
		`<=4w ${fmt((nShort / N) * 100, 1)}%`,
		`>=50w ${fmt((nLong / N) * 100, 1)}%`,
		`bimodality ${fmt(K / N, 3)}`,
		r === null
			? 'drift n/a'
			: `positional r=${fmt(r, 2)} (lower 90% bound ${fmt(bound, 2)}, ${J} ${unitKind})`,
	];
	return scoredResult(def, {
		K,
		E: N,
		raw: fit.raw,
		estimate: fit.estimate,
		ci90: fit.ci90,
		deviation: fit.deviation,
		phi,
		kappa,
		score,
		scoreCi90: [Math.min(...scoreCi90), Math.max(...scoreCi90)],
		perParagraph: { k, e },
		evidence: ev.build(),
		notes,
		features: {
			bimodality: K / N,
			shortShare: nShort / N,
			longShare: nLong / N,
			driftAvailable: subB === null ? 0 : 1,
			driftR: r ?? 0,
			driftBound: bound,
			driftUnits: J,
			subScoreBimodality: subA,
			subScoreDrift: subB ?? 0,
		},
		summary,
	});
}

export const proc06: ChannelImpl = { def: proc06Def, phase: 'event', run };
